import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { environment } from '../../../environments/environment';
import { getFirebaseConfig } from './firebase.config';

/**
 * Bootstraps the Firebase Web SDK for the browser.
 *
 * Step 1 of FCM integration: application initialization only.
 * Token registration and notification permissions are handled in later steps.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private readonly platformId = inject(PLATFORM_ID);

  /** Cached default Firebase app instance (browser only). */
  private app: FirebaseApp | null = null;

  /**
   * Initializes Firebase once per browser session.
   * Safe to call multiple times; reuses the existing default app.
   * No-ops during SSR where the Web SDK is unavailable.
   */
  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.app) {
      return;
    }

    // Guard against duplicate default-app initialization (e.g. HMR or double bootstrap).
    if (getApps().length > 0) {
      this.app = getApp();
      this.logInitializedApp('reused existing Firebase app');
      return;
    }

    const config = getFirebaseConfig();
    this.app = initializeApp(config);
    this.logInitializedApp('initialized new Firebase app');
  }

  /** Dev-only log; reads projectId from the live Firebase app instance. */
  private logInitializedApp(source: string): void {
    if (environment.production || !this.app) {
      return;
    }

    console.info(`[Firebase] Application initialized (${source})`, {
      projectId: this.app.options.projectId,
    });
  }

  /** Returns the initialized Firebase app, or null when running on the server or before init. */
  getApp(): FirebaseApp | null {
    return this.app;
  }

  /** Whether the Firebase default app has been initialized in this browser session. */
  isInitialized(): boolean {
    return this.app !== null;
  }
}
